import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi } from '../../context';
import { Confirm, Failure, Field, Loading, OnceBox } from '../../bits';
import { Chip, Panel, useToast } from '../../kit';
import type { ChipTone } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type { IntakeMinted, IntakeRead, PageTemplate } from '../../../wire';
import type { ApiFailure } from '../../../api/errors';
import { Steps } from './Steps';
import { SiteDialog } from './SiteDialog';
import { languageName, shortDate, templateLabel } from './words';

/**
 * **The questionnaire** — the owner's private form, as one card that says
 * where it is (`docs/tenant-intake-design.md`).
 *
 * It replaces seven blocks stacked in one card — a status pill, a re-mint
 * form, the review table, the menu, the book, the draft hand-off and the
 * photographs — with a step track, a progress line, three facts and the
 * buttons that move it. Reading the answers is **one guided screen** a click
 * away (`AnswersReview`, at `#/b/<id>/site/answers`); the photographs are their
 * own card beside this one.
 *
 * Three properties carried over unchanged, because they were right:
 *
 *  * **The link is shown once**, in the `OnceBox` a key uses, with the message
 *    an operator actually sends beside it — in the *owner's* language, the one
 *    place on this surface where the console's own locale does not decide.
 *  * **A new link is confirmed**, in words that say the link on the owner's
 *    phone stops working. The first link is not: nothing exists to break.
 *  * **Closing is confirmed.** Extending and reopening are not — both are
 *    undone by clicking the other button.
 *
 * The step track is derived, never stored. "Sent" cannot be known (the link
 * goes out by the operator's own hand), so a link that exists counts as sent;
 * "Opened" is the first save, which is when the server first computes
 * progress; "Answered" is the owner pressing send; and "Used on the page" is a
 * page published after they did — the one signal the console has that their
 * words reached a guest.
 */
export function Questionnaire({
  tenantId,
  intake,
  absent,
  loading,
  failure,
  templates,
  publishedAt,
  onChanged,
  onReview,
}: {
  tenantId: string;
  intake: IntakeRead | null;
  /** `404` — no link was ever made, which is a state and not a fault. */
  absent: boolean;
  loading: boolean;
  failure: ApiFailure | null;
  templates: readonly PageTemplate[];
  /** When their page was last published, for the last step. */
  publishedAt: string | null;
  onChanged: () => void;
  onReview: () => void;
}): ReactNode {
  const t = useT();
  const [minted, setMinted] = useState<IntakeMinted | null>(null);

  const chip = intake === null ? null : chipOf(t, intake, publishedAt);

  return (
    <Panel
      title={t('site.q.title')}
      className="site-card"
      actions={chip === null ? undefined : <Chip tone={chip.tone}>{chip.label}</Chip>}
    >
      {failure !== null && !absent ? <Failure failure={failure} /> : null}

      {minted !== null ? (
        <MintedLink
          minted={minted}
          onDismiss={() => {
            setMinted(null);
            onChanged();
          }}
        />
      ) : null}

      {intake === null ? (
        loading ? (
          <Loading what={t('loading.intake')} />
        ) : absent ? (
          <FirstLink tenantId={tenantId} templates={templates} onMinted={setMinted} />
        ) : null
      ) : (
        <Sent
          tenantId={tenantId}
          intake={intake}
          templates={templates}
          publishedAt={publishedAt}
          onMinted={setMinted}
          onChanged={onChanged}
          onReview={onReview}
        />
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Where it is                                                          */
/* ------------------------------------------------------------------ */

function submitted(intake: IntakeRead): boolean {
  return intake.status === 'submitted' || intake.submitted_at !== null;
}

function used(intake: IntakeRead, publishedAt: string | null): boolean {
  return (
    intake.submitted_at !== null &&
    publishedAt !== null &&
    Date.parse(publishedAt) > Date.parse(intake.submitted_at)
  );
}

/** The latest step reached, as an index into `stepsOf`. */
export function reachedOf(intake: IntakeRead, publishedAt: string | null): number {
  if (used(intake, publishedAt)) return 4;
  if (submitted(intake)) return 3;
  if (intake.progress.total > 0) return 2;
  return 1;
}

function chipOf(
  t: T,
  intake: IntakeRead,
  publishedAt: string | null,
): { tone: ChipTone | undefined; label: string } {
  if (intake.status === 'closed') return { tone: undefined, label: t('site.q.chip.closed') };
  if (used(intake, publishedAt)) return { tone: 'ok', label: t('site.q.chip.used') };
  if (submitted(intake)) return { tone: 'ok', label: t('site.q.chip.answered') };
  if (Date.parse(intake.expires_at) < Date.now()) {
    return { tone: 'danger', label: t('site.q.chip.expired') };
  }
  if (intake.progress.total === 0) return { tone: 'warn', label: t('site.q.chip.notOpened') };
  return { tone: 'accent', label: t('site.q.chip.filling') };
}

function Sent({
  tenantId,
  intake,
  templates,
  publishedAt,
  onMinted,
  onChanged,
  onReview,
}: {
  tenantId: string;
  intake: IntakeRead;
  templates: readonly PageTemplate[];
  publishedAt: string | null;
  onMinted: (minted: IntakeMinted) => void;
  onChanged: () => void;
  onReview: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const toast = useToast();
  const action = useAction();
  const [extending, setExtending] = useState(false);
  const [days, setDays] = useState(30);
  const [closing, setClosing] = useState(false);
  const [reminting, setReminting] = useState(false);

  const { answered, total } = intake.progress;
  const percent = total === 0 ? 0 : Math.round((answered / total) * 100);

  const run = async (work: () => Promise<unknown>, done: string): Promise<void> => {
    const ok = await action.run(async () => {
      await work();
    });
    if (ok) {
      setClosing(false);
      setExtending(false);
      toast(done);
      onChanged();
    }
  };

  return (
    <>
      <Steps
        label={t('site.q.title')}
        steps={[
          t('site.q.step.made'),
          t('site.q.step.sent'),
          t('site.q.step.opened'),
          t('site.q.step.answered'),
          t('site.q.step.used'),
        ]}
        reached={reachedOf(intake, publishedAt)}
      />

      <div
        className="site-progress"
        role="progressbar"
        aria-label={t('site.q.answers')}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={answered}
      >
        <i style={{ width: `${String(percent)}%` }} />
      </div>

      <dl className="site-kv">
        <dt>{t('site.q.answers')}</dt>
        <dd>
          {total === 0 ? t('site.q.notOpened') : t('site.q.progress', { answered, total })}
          {intake.status === 'submitted' ? (
            <span className="muted">
              {' '}
              · {t('site.q.sentBack', { day: shortDate(intake.submitted_at, locale) })}
            </span>
          ) : null}
        </dd>
        <dt>{t('site.q.photos')}</dt>
        <dd>
          {intake.uploads === 0 ? (
            <span className="muted">{t('site.q.noPhotos')}</span>
          ) : (
            t('site.q.photoCount', { count: intake.uploads })
          )}
        </dd>
        {intake.status === 'closed' ? null : (
          <>
            <dt>{t('site.q.until')}</dt>
            <dd>
              {shortDate(intake.expires_at, locale)}{' '}
              {extending ? null : (
                <button
                  type="button"
                  className="button button--quiet button--sm"
                  onClick={() => {
                    action.clear();
                    setExtending(true);
                  }}
                >
                  {t('site.q.extend')}
                </button>
              )}
            </dd>
          </>
        )}
        <dt>{t('site.q.form')}</dt>
        <dd>
          {t('site.q.formIs', {
            template: templateLabel(templates, intake.template),
            language: languageName(t, intake.locale),
          })}
        </dd>
      </dl>

      {extending ? (
        <div className="row-actions site-inline">
          <label className="inline-field">
            <span>{t('site.q.extendBy')}</span>
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label={t('site.q.extendBy')}
            />
          </label>
          <button
            type="button"
            className="button button--primary button--sm"
            disabled={action.busy}
            onClick={() =>
              void run(() => api.extendIntake(tenantId, days), t('site.q.extended', { days }))
            }
          >
            {action.busy ? t('common.working') : t('site.q.extendGo', { days })}
          </button>
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={() => setExtending(false)}
          >
            {t('common.cancel')}
          </button>
        </div>
      ) : null}

      {!closing && !reminting && action.failure !== null ? (
        <Failure failure={action.failure} />
      ) : null}

      <div className="row-actions">
        {total > 0 || submitted(intake) ? (
          <button type="button" className="button button--primary button--sm" onClick={onReview}>
            {t('site.q.review')}
          </button>
        ) : null}
        <button
          type="button"
          className="button button--sm"
          onClick={() => {
            action.clear();
            setReminting(true);
          }}
        >
          {t('intake.remint')}
        </button>
        {intake.status === 'submitted' ? (
          <button
            type="button"
            className="button button--quiet button--sm"
            disabled={action.busy}
            onClick={() => void run(() => api.reopenIntake(tenantId), t('site.q.reopened'))}
          >
            {t('site.q.reopen')}
          </button>
        ) : null}
        {intake.status === 'closed' ? null : (
          <button
            type="button"
            className="button button--quiet button--sm"
            onClick={() => {
              action.clear();
              setClosing(true);
            }}
          >
            {t('site.q.close')}
          </button>
        )}
      </div>

      {total > 0 || submitted(intake) ? null : (
        <p className="muted site-note">{t('site.q.reviewLater')}</p>
      )}

      {reminting ? (
        <NewLink
          tenantId={tenantId}
          templates={templates}
          current={intake.template}
          currentLocale={intake.locale}
          onMinted={(next) => {
            setReminting(false);
            onMinted(next);
          }}
          onCancel={() => setReminting(false)}
        />
      ) : null}

      {closing ? (
        <Confirm
          title={t('site.q.closeTitle')}
          consequence={t('site.q.closeConsequence')}
          confirmLabel={t('site.q.close')}
          busy={action.busy}
          failure={action.failure}
          onConfirm={() => void run(() => api.closeIntake(tenantId), t('site.q.closed'))}
          onCancel={() => {
            action.clear();
            setClosing(false);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Making the link                                                     */
/* ------------------------------------------------------------------ */

/** Template, language and how long it works — the three things a link is. */
function LinkFields({
  templates,
  template,
  locale,
  days,
  onTemplate,
  onLocale,
  onDays,
}: {
  templates: readonly PageTemplate[];
  template: string;
  locale: string;
  days: number;
  onTemplate: (value: string) => void;
  onLocale: (value: string) => void;
  onDays: (value: number) => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="form__row">
      <Field label={t('intake.template')} hint={t('intake.templateHint')}>
        <select
          value={template}
          onChange={(e) => onTemplate(e.target.value)}
          aria-label={t('intake.template')}
        >
          {templates.length === 0 ? <option value={template}>{template}</option> : null}
          {templates.map((one) => (
            <option key={one.name} value={one.name}>
              {one.label} · {one.vertical}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t('intake.language')} hint={t('intake.languageHint')}>
        <select
          value={locale}
          onChange={(e) => onLocale(e.target.value)}
          aria-label={t('intake.language')}
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </Field>
      <Field label={t('site.q.days')}>
        <input
          type="number"
          min={1}
          max={365}
          value={days}
          onChange={(e) => onDays(Number(e.target.value))}
          aria-label={t('site.q.days')}
        />
      </Field>
    </div>
  );
}

/** No link yet: a sentence, the three fields and the button. Not destructive. */
function FirstLink({
  tenantId,
  templates,
  onMinted,
}: {
  tenantId: string;
  templates: readonly PageTemplate[];
  onMinted: (minted: IntakeMinted) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [template, setTemplate] = useState(templates[0]?.name ?? 'one-room');
  const [locale, setLocale] = useState('en');
  const [days, setDays] = useState(30);

  return (
    <>
      <p>{t('site.q.none')}</p>
      <p className="muted">{t('intake.notSentNote')}</p>
      <div className="form">
        <LinkFields
          templates={templates}
          template={template}
          locale={locale}
          days={days}
          onTemplate={setTemplate}
          onLocale={setLocale}
          onDays={setDays}
        />
        {action.failure !== null ? <Failure failure={action.failure} /> : null}
        <div className="row-actions">
          <button
            type="button"
            className="button button--primary"
            disabled={action.busy}
            onClick={() =>
              void action.run(async () => {
                onMinted(await api.mintIntake(tenantId, { template, locale, ttl_days: days }));
              })
            }
          >
            {action.busy ? t('common.working') : t('intake.mint')}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * A new link: the same three fields, and the consequence in the same dialog —
 * the link already on the owner's phone stops working. Cancelling asks the
 * server nothing.
 */
function NewLink({
  tenantId,
  templates,
  current,
  currentLocale,
  onMinted,
  onCancel,
}: {
  tenantId: string;
  templates: readonly PageTemplate[];
  current: string;
  currentLocale: string;
  onMinted: (minted: IntakeMinted) => void;
  onCancel: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [template, setTemplate] = useState(current);
  const [locale, setLocale] = useState(currentLocale);
  const [days, setDays] = useState(30);

  return (
    <SiteDialog title={t('site.q.remintTitle')} busy={action.busy} onClose={onCancel}>
      <p className="modal__consequence">{t('intake.remintConsequence')}</p>
      <div className="form">
        <LinkFields
          templates={templates}
          template={template}
          locale={locale}
          days={days}
          onTemplate={setTemplate}
          onLocale={setLocale}
          onDays={setDays}
        />
      </div>
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      <div className="modal__actions">
        <button type="button" className="button" onClick={onCancel} disabled={action.busy}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--danger"
          disabled={action.busy}
          onClick={() =>
            void action.run(async () => {
              onMinted(await api.mintIntake(tenantId, { template, locale, ttl_days: days }));
            })
          }
        >
          {action.busy ? t('common.working') : t('intake.remint')}
        </button>
      </div>
    </SiteDialog>
  );
}

/**
 * The link, once — plus the message an operator actually sends, in the
 * owner's language. Two copy buttons, because half the time the link alone is
 * what is wanted (the `OnceBox` has that one).
 */
function MintedLink({
  minted,
  onDismiss,
}: {
  minted: IntakeMinted;
  onDismiss: () => void;
}): ReactNode {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const message = minted.locale.toLowerCase().startsWith('es')
    ? t('intake.message.es', { link: minted.link })
    : t('intake.message.en', { link: minted.link });

  const copy = (): void => {
    void navigator.clipboard
      ?.writeText(message)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <>
      <OnceBox
        label={t('intake.linkLabel', { host: minted.host })}
        value={minted.link}
        note={minted.link_note}
        onDismiss={onDismiss}
      />
      <div className="field field--wide">
        <span className="field__label">{t('intake.messageLabel')}</span>
        <textarea readOnly rows={3} value={message} aria-label={t('intake.messageLabel')} />
        <div className="row-actions">
          <button type="button" className="button button--sm" onClick={copy}>
            {copied ? t('once.copied') : t('intake.copyMessage')}
          </button>
        </div>
      </div>
      {minted.warnings.map((warning) => (
        <p className="warn" key={warning}>
          {warning}
        </p>
      ))}
    </>
  );
}
